// «Stock por tienda» en tiempo real: en tienda, apartados y para vender, con piezas y acciones.
import { useState } from 'react';
import { Button } from '@/kit';
import { cn } from '@/lib/cn';
import type { InventoryLensPeriod } from '@/api/services/retail-catalog/backoffice';
import {
    CellText,
    DataTable,
    NO_CELL_VALUE,
    type DataTableColumn,
} from '@/shared/patterns/DataTable';
import { reportPeriodLabel } from '@/shared/patterns/PeriodBand';
import { StatusPill } from '@/shared/patterns/Pills';
import {
    distributionTone,
    reservedCountLabel,
    type DistributionTone,
    type StoreDistributionRow,
} from '../../lib/inventoryDetail';
import type { MovementKind } from '../../lib/inventoryMovements';
import { DetailCard } from './DetailCard';
import { StorePiecesModal } from './StorePiecesModal';

const TONE_STOCK: Record<DistributionTone, string> = {
    zero: 'text-danger',
    low: 'text-warning',
    ok: 'text-foreground',
};

const NO_RESERVED = '—';

const FIGURE_WIDTH = 11;
const SOLD_WIDTH = 16;
const ACTION_WIDTH = 17;

const SMALL_BUTTON = 'h-6 px-2 text-[11px]';

/** Movimientos que el rol puede abrir desde la tarjeta. */
export interface StoreStockActions {
    entry: boolean;
    adjustment: boolean;
    transfer: boolean;
    onOpen: (kind: MovementKind, storeId?: string) => void;
}

interface ColumnOptions {
    period: InventoryLensPeriod;
    zones: ReadonlyMap<string, string>;
    inbound: ReadonlyMap<string, number>;
    showSold: boolean;
    onOpenPieces: ((store: StoreDistributionRow) => void) | null;
    onTransfer: ((storeId: string) => void) | null;
}

/** «En tienda» abre las piezas con teclado cuando las hay; el clic en la fila es solo un atajo. */
function OnHandCell({
    store,
    onOpenPieces,
}: {
    store: StoreDistributionRow;
    onOpenPieces: ((store: StoreDistributionRow) => void) | null;
}) {
    if (!onOpenPieces || store.pieces.length === 0) return <>{store.onHand}</>;
    return (
        <button
            type="button"
            aria-label={`Ver piezas de ${store.name}`}
            onClick={(event) => {
                event.stopPropagation();
                onOpenPieces(store);
            }}
            className="rounded px-1 tabular-nums underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
        >
            {store.onHand}
        </button>
    );
}

/** Columnas de la tabla; Tienda toma lo que dejan las demás y cada columna opcional solo con dato. */
function storeColumns(o: ColumnOptions): DataTableColumn<StoreDistributionRow>[] {
    const storeWidth =
        100 - 3 * FIGURE_WIDTH - (o.showSold ? SOLD_WIDTH : 0) - (o.onTransfer ? ACTION_WIDTH : 0);
    const { onOpenPieces, onTransfer } = o;
    const columns: DataTableColumn<StoreDistributionRow>[] = [
        {
            key: 'store',
            label: 'Tienda',
            width: `${String(storeWidth)}%`,
            // Dos renglones en toda tienda: la zona se reserva con «—» si falta (DEC-47).
            cellOverflow: 'wrap',
            cell: (store) => {
                const zone = o.zones.get(store.storeId);
                const inbound = o.inbound.get(store.storeId) ?? 0;
                return (
                    <>
                        <span className="flex min-w-0 items-baseline gap-1">
                            <span className="truncate font-semibold">{store.name}</span>
                            <span className="flex-none font-mono text-[9.5px] text-muted-foreground">
                                {store.code}
                            </span>
                        </span>
                        <CellText className="text-[9.5px] text-muted-foreground">
                            {zone ?? NO_CELL_VALUE}
                            {inbound > 0 ? (
                                <span className="font-semibold text-info">
                                    {` · recibe ${String(inbound)} en tránsito`}
                                </span>
                            ) : null}
                        </CellText>
                    </>
                );
            },
            cellTitle: (store) => {
                const zone = o.zones.get(store.storeId);
                return zone ? `${store.name} · ${zone}` : store.name;
            },
        },
        {
            key: 'onHand',
            label: 'En tienda',
            width: `${String(FIGURE_WIDTH)}%`,
            numeric: true,
            note: 'Unidades físicas en la tienda: para vender más apartadas',
            cell: (store) => <OnHandCell store={store} onOpenPieces={onOpenPieces} />,
        },
        {
            key: 'reserved',
            label: 'Apartados',
            width: `${String(FIGURE_WIDTH)}%`,
            numeric: true,
            cellOverflow: 'nowrap',
            note: 'Unidades apartadas en ventas en curso',
            cell: (store) =>
                store.reserved === 0 ? (
                    NO_RESERVED
                ) : (
                    <StatusPill tone="warning" title={reservedCountLabel(store.reserved)}>
                        {store.reserved}
                    </StatusPill>
                ),
        },
        {
            key: 'forSale',
            label: 'Para vender',
            width: `${String(FIGURE_WIDTH)}%`,
            numeric: true,
            cell: (store) => store.forSale,
            cellClassName: (store) => cn('font-bold', TONE_STOCK[distributionTone(store.forSale)]),
        },
    ];
    if (o.showSold) {
        columns.push({
            key: 'sold',
            label: `Vendidas · ${reportPeriodLabel(o.period).toLowerCase()}`,
            width: `${String(SOLD_WIDTH)}%`,
            numeric: true,
            note: 'Solo de referencia: lo vendido ya no cuenta en el stock',
            cell: (store) => (store.sold === null ? '—' : store.sold),
            cellClassName: () => 'text-muted-foreground',
        });
    }
    if (onTransfer) {
        columns.push({
            key: 'transfer',
            label: <span className="sr-only">Acción</span>,
            width: `${String(ACTION_WIDTH)}%`,
            numeric: true,
            cellOverflow: 'nowrap',
            cell: (store) => (
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-6 px-1.5 text-[10.5px]"
                    disabled={store.forSale === 0}
                    title={store.forSale === 0 ? 'Sin piezas para vender que traspasar' : undefined}
                    aria-label={`Traspasar desde ${store.name}`}
                    onClick={(event) => {
                        event.stopPropagation();
                        onTransfer(store.storeId);
                    }}
                >
                    Traspasar
                </Button>
            ),
        });
    }
    return columns;
}

export function StoreStockCard({
    rows,
    period,
    zones,
    inbound,
    actions,
}: {
    /** Renglones de `storeDistributionRows`, que también dan los totales de la pestaña. */
    rows: readonly StoreDistributionRow[];
    period: InventoryLensPeriod;
    /** Zona por tienda, del padrón de retail-org. */
    zones: ReadonlyMap<string, string>;
    /** Piezas en tránsito hacia cada tienda. */
    inbound: ReadonlyMap<string, number>;
    /** `null` sin acciones: kit o un rol que no registra movimientos. */
    actions: StoreStockActions | null;
}) {
    const [selected, setSelected] = useState<StoreDistributionRow | null>(null);
    // Solo un SKU con piezas identificadas abre el modal; uno por cantidad no tiene qué mostrar.
    const hasPieces = rows.some((store) => store.pieces.length > 0);
    const columns = storeColumns({
        period,
        zones,
        inbound,
        showSold: rows.some((store) => store.sold !== null),
        onOpenPieces: hasPieces ? setSelected : null,
        onTransfer: actions?.transfer
            ? (storeId) => {
                  actions.onOpen('TRANSFER', storeId);
              }
            : null,
    });
    const header =
        actions && (actions.entry || actions.adjustment) ? (
            <span className="flex items-center gap-1.5">
                tiempo real
                {actions.entry ? (
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className={SMALL_BUTTON}
                        onClick={() => {
                            actions.onOpen('ENTRY');
                        }}
                    >
                        + Entrada
                    </Button>
                ) : null}
                {actions.adjustment ? (
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className={SMALL_BUTTON}
                        onClick={() => {
                            actions.onOpen('ADJUSTMENT');
                        }}
                    >
                        Ajuste
                    </Button>
                ) : null}
            </span>
        ) : (
            'tiempo real'
        );
    return (
        <DetailCard title="Stock por tienda" aside={header} flush>
            <DataTable
                caption="Stock por tienda"
                columns={columns}
                rows={rows}
                rowKey={(store) => store.storeId}
                density="compact"
                onRowClick={
                    hasPieces
                        ? (store) => {
                              if (store.pieces.length > 0) setSelected(store);
                          }
                        : undefined
                }
                rowTitle={(store) =>
                    store.pieces.length > 0 ? 'Ver piezas de la tienda' : undefined
                }
                // Una tienda sin piezas no abre nada: sin cursor de clic ni resaltado.
                rowClassName={(store) =>
                    hasPieces && store.pieces.length === 0
                        ? 'cursor-default hover:bg-transparent'
                        : undefined
                }
                empty="Sin tiendas con este SKU."
            />
            <StorePiecesModal
                store={selected}
                onClose={() => {
                    setSelected(null);
                }}
            />
        </DetailCard>
    );
}
